import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Login = () => {
  const { signInWithGoogle, signInWithEmail, signUpWithEmail, sendVerificationEmail, sendCustomVerificationEmail, checkEmailVerification } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Helper function to get redirect path
  const getRedirectPath = () => {
    const redirectParam = searchParams.get('redirect');
    return redirectParam && redirectParam !== '/login' ? redirectParam : '/';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    
    try {
      if (isLogin) {
        const result = await signInWithEmail(email, password);
        
        // Check verification status using our custom function
        try {
          const verificationStatus = await checkEmailVerification();
          if (!verificationStatus.isVerified) {
            setShowVerificationPrompt(true);
            setMessage('Please verify your email address before continuing. Check your inbox for a verification link.');
            return;
          }
        } catch (error) {
          console.error('Error checking email verification:', error);
          // Fallback to Firebase Auth verification status
          if (result.user && !result.user.emailVerified) {
            setShowVerificationPrompt(true);
            setMessage('Please verify your email address before continuing. Check your inbox for a verification link.');
            return;
          }
        }
        
        // Redirect to original page or homepage after successful login
        navigate(getRedirectPath());
      } else {
        // Validate password confirmation
        if (password !== confirmPassword) {
          setError('Passwords do not match. Please try again.');
          return;
        }

        const userProfile = {
          firstName,
          lastName,
          phoneNumber
        };
        await signUpWithEmail(email, password, userProfile, true);
        
        // Send our custom verification email
        try {
          await sendCustomVerificationEmail(false);
          setMessage('Account created successfully! Please check your email for a verification link before signing in.');
        } catch (error) {
          console.error('Error sending custom verification email:', error);
          setMessage('Account created successfully! Please check your email for a verification link before signing in.');
        }
        
        setIsLogin(true);
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setFirstName('');
        setLastName('');
        setPhoneNumber('');
      }
    } catch (error) {
      let errorMessage = error.message;
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'An account with this email already exists. Please sign in instead.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters long.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Email and/or password incorrect. Please try again.';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Email and/or password incorrect. Please try again.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed login attempts. Please try again later or reset your password.';
      }
      
      setError(errorMessage);
    }
  };

  const handleResendVerification = async () => {
    try {
      // Try our custom verification email first
      try {
        await sendCustomVerificationEmail(true);
        setMessage('Verification email sent! Please check your inbox.');
      } catch (error) {
        console.error('Error sending custom verification email:', error);
        // Fallback to Firebase Auth verification
        const result = await signInWithEmail(email, password);
        if (result.user) {
          await sendVerificationEmail(result.user);
          setMessage('Verification email sent! Please check your inbox.');
        }
      }
    } catch (error) {
      setError('Failed to send verification email. Please try again.');
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    try {
      const result = await signInWithGoogle();
      if (result.user) {
        // Redirect to original page or homepage after successful Google sign in
        navigate(getRedirectPath());
      }
    } catch (error) {
      setError(error.message);
    }
  };

  return (
    <div className="login-container">
      <form onSubmit={handleSubmit} className="login-form">
        <h2>{isLogin ? 'Login' : 'Sign Up'}</h2>
        
        {!isLogin && (
          <>
            <input
              type="text"
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
            
            <input
              type="text"
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
            
            <input
              type="tel"
              placeholder="Phone Number (optional)"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          </>
        )}
        
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        
        <div className="password-input-container">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button
            type="button"
            className="password-toggle-btn"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? "🙈" : "👁️"}
          </button>
        </div>
        
        {!isLogin && (
          <div className="password-input-container">
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
            >
              {showConfirmPassword ? "🙈" : "👁️"}
            </button>
          </div>
        )}
        
        {!isLogin && (
          <div className="password-requirements">
            <p className="requirements-title">Password must contain:</p>
            <ul className="requirements-list">
              <li className={password.length >= 8 && password.length <= 20 ? 'valid' : 'invalid'}>
                8-20 characters
              </li>
              <li className={/[A-Z]/.test(password) ? 'valid' : 'invalid'}>
                At least one uppercase letter
              </li>
              <li className={/[a-z]/.test(password) ? 'valid' : 'invalid'}>
                At least one lowercase letter
              </li>
              <li className={/\d/.test(password) ? 'valid' : 'invalid'}>
                At least one number
              </li>
            </ul>
          </div>
        )}
        
        {error && <p className="error">{error}</p>}
        {message && <p className="message">{message}</p>}
        
        {showVerificationPrompt && (
          <div className="verification-prompt">
            <p>Your email is not verified. You need to verify your email before you can access your account.</p>
            <button type="button" onClick={handleResendVerification} className="resend-btn">
              Resend Verification Email
            </button>
          </div>
        )}
        
        <button type="submit">
          {isLogin ? 'Login' : 'Sign Up'}
        </button>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <button type="button" onClick={handleGoogleSignIn} className="google-signin-btn">
          <img src="https://developers.google.com/identity/images/g-logo.png" alt="Google" className="google-icon" />
          Continue with Google
        </button>
        
        <p>
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <button 
            type="button" 
            onClick={() => setIsLogin(!isLogin)}
            className="toggle-auth"
          >
            {isLogin ? 'Sign Up' : 'Login'}
          </button>
        </p>
      </form>
    </div>
  );
};

export default Login;